import React from 'react';
import type { ClientViewDealer, ClientStatusDetails, ClientStatusRecord } from '../../api/types';
import {
  PRIORITY_ORDER, PRIORITY_LABEL, PRIORITY_COLOR,
  TIER_LABEL, TIER_COLOR,
  METRIC_GRAIN_LABEL, METRIC_GRAIN_PRIOR_LABEL, DEFAULT_METRIC_GRAIN,
  type MetricGrain,
  classifyTier,
} from './clientViewTokens';
import { ClientStatusControl } from './ClientStatusControl';
import { REASON_CODE_LABEL } from './clientStatusVocab';
import { RepTag, SalesforceLink } from '../shared/SalesforceLink';

interface DealerCardProps {
  dealer: ClientViewDealer;
  selected: boolean;
  onClick: () => void;
  /** The grain the KPIs are read on, so the labels cannot claim the wrong window. */
  grain?: MetricGrain;
  /** Rep-assigned client status. Omit to hide the control entirely. */
  status?: ClientStatusRecord | null;
  statusOptions?: string[];
  reasonCodes?: string[];
  permanentStatuses?: string[];
  onSetStatus?: (sellerId: string, status: string, details: ClientStatusDetails) => void;
  onClearStatus?: (sellerId: string) => void;
}

export const ClientViewDealerCard: React.FC<DealerCardProps> = ({
  dealer,
  selected,
  onClick,
  grain = DEFAULT_METRIC_GRAIN,
  status,
  statusOptions,
  reasonCodes = [],
  permanentStatuses = [],
  onSetStatus,
  onClearStatus,
}) => {
  const grainLabel = METRIC_GRAIN_LABEL[grain].toLowerCase();
  const priorLabel = METRIC_GRAIN_PRIOR_LABEL[grain];
  const tier = classifyTier(dealer.core);
  // RDS-1.2: the standing follows the Quarter grain; every other grain reads the rolling period.
  const coreWindowLabel = dealer.core?.core_window === 'quarter' ? 'this quarter' : 'the rolling 3-month period';
  const totalPriority = PRIORITY_ORDER.reduce(
    (sum, k) => sum + (dealer.priority_counts?.[k] || 0),
    0,
  );

  const daysSince = dealer.days_since_last_contact;
  const contactLine =
    daysSince === null || daysSince === undefined
      ? 'No SFDC contact'
      : `Last contact ${daysSince}d ago · ${dealer.sfdc_contact_count_30d ?? 0} in 30d`;

  const cm = dealer.core_metrics;
  const inspChange = computeChange(cm?.inspections_current, cm?.inspections_prior);
  // Compare on the displayed (rounded) value so a tiny prior shown as the same
  // value doesn't produce a misleading 100% change.
  const convChange =
    cm != null && fmtPct(cm.mmi_conversion_current) === fmtPct(cm.mmi_conversion_prior)
      ? ({ dir: 'flat', pct: null } as Change)
      : computeChange(cm?.mmi_conversion_current, cm?.mmi_conversion_prior);

  const inspToCore = dealer.core?.inspections_needed_for_core;
  const salesToCore = dealer.core?.net_sales_needed_for_core;
  const isCore = inspToCore == null && salesToCore == null;

  const locationStr = [dealer.territory_city, dealer.territory_state].filter(Boolean).join(', ');
  const showStatus = Boolean(statusOptions?.length && onSetStatus && onClearStatus);

  return (
    // A div, not a button: the status control below is itself a button, and a
    // button cannot legally nest inside one (React renders it, the browser
    // reparents it, and the click handlers stop firing).
    <div
      role="button"
      tabIndex={0}
      data-usage-seller-uid={dealer.seller_id}
      data-usage-section="client_card"
      className={`cv-card ${selected ? 'selected' : ''}`}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <div className="cv-card-header">
        <div className="cv-card-title">
          <span className="cv-card-name">{dealer.seller_name || dealer.seller_id}</span>
          {locationStr && <span className="cv-card-location-inline">· {locationStr}</span>}
          <span className="cv-card-sid">· {dealer.seller_id}</span>
        </div>
        <span
          className="cv-card-tier-badge"
          style={{ background: TIER_COLOR[tier] }}
          title={`${TIER_LABEL[tier]}, measured on ${coreWindowLabel}`}
        >
          {TIER_LABEL[tier]}
        </span>
      </div>
      {dealer.distance_miles !== null && dealer.distance_miles !== undefined && (
        <div className="cv-card-submeta">
          <span className="cv-card-distance">{dealer.distance_miles} mi</span>
        </div>
      )}

      <div className="cv-card-kpis">
        <div className="cv-card-kpi" title={`Core Client inspections in the selected grain (${grainLabel}), vs ${priorLabel}. Counted the way reporting and comp count them: one per vehicle, per dealer, per calendar month, so re-inspecting the same car that month does not count again. One of the two Core Client gates (needs 18+, measured on the rolling 3-month period).`}>
          <div className="cv-card-kpi-label">Inspections</div>
          <div className="cv-card-kpi-row">
            <span className="cv-card-kpi-value">{cm?.inspections_current ?? 0}</span>
            <ChangeBadge change={inspChange} />
          </div>
        </div>
        <div className="cv-card-kpi" title={`MMI conversion for the selected grain (${grainLabel}), vs ${priorLabel}: net sales closing in the window on previously-inspected vehicles, divided by the inspections shown (one per vehicle per month). Core Client standing itself is measured on the rolling 3-month period (needs 25%+). Not the inspection-funnel rate.`}>
          <div className="cv-card-kpi-label">MMI Conv</div>
          <div className="cv-card-kpi-row">
            <span className="cv-card-kpi-value">{fmtPct(cm?.mmi_conversion_current)}</span>
            <ChangeBadge change={convChange} />
          </div>
        </div>
        <div className="cv-card-kpi" title={`What is still needed to reach Core Client on ${coreWindowLabel}: Insp = additional inspections (one per vehicle per month) toward the 18 threshold; Sales = additional NET sales on previously-inspected vehicles toward the 25% conversion threshold. 'Met' means that gate is already cleared. The Quarter grain reads the quarter; every other grain reads the rolling 3-month period.`}>
          <div className="cv-card-kpi-label">To Core</div>
          <div className="cv-card-kpi-row">
            {isCore ? (
              <span className="cv-card-kpi-value cv-card-kpi-value-sm">Core ✓</span>
            ) : (
              <span className="cv-card-coregap">
                <span className="cv-card-coregap-part">
                  <span className="cv-card-coregap-tag">Insp</span> {coreGapValue(inspToCore)}
                </span>
                <span className="cv-card-coregap-part">
                  <span className="cv-card-coregap-tag">Sales</span> {coreGapValue(salesToCore)}
                </span>
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="cv-card-priority-row" aria-label={`${totalPriority} priority VINs`}>
        {PRIORITY_ORDER.map((k) => {
          const count = dealer.priority_counts?.[k] || 0;
          const color = PRIORITY_COLOR[k];
          return (
            <span
              key={k}
              className={`cv-card-priority-badge ${count === 0 ? 'zero' : ''}`}
              style={{ background: color.bg, color: color.fg }}
              title={`${PRIORITY_LABEL[k]}: ${count}`}
            >
              {(k === 'active_gt_1_day' ? 'Active > 3d' : PRIORITY_LABEL[k].split(' ')[0])} {count}
            </span>
          );
        })}
      </div>

      <div className="cv-card-contact">{contactLine}</div>

      {(showStatus || dealer.salesforce_id || dealer.rds_rep) && (
        <div className="cv-card-status">
          {showStatus && status?.status && (
            <span
              className={`cv-card-status-chip${status.is_expired ? ' stale' : ''}`}
              title={
                status.is_expired
                  ? 'Past its useful life — worth another look.'
                  : `Set by ${status.user_email ?? 'a rep'}.`
              }
            >
              {status.age_days != null ? `${status.age_days}d ago` : 'set'}
            </span>
          )}
          {showStatus && (status?.reason_code || status?.reason_text) && (
            <span
              className="cv-card-status-reason"
              title={status.reason_text || 'No note was left.'}
            >
              {status.reason_code
                ? (REASON_CODE_LABEL[status.reason_code] ?? status.reason_code)
                : 'Note'}
            </span>
          )}
          {showStatus && (
            <ClientStatusControl
              variant="card"
              value={status}
              options={statusOptions ?? []}
              reasonCodes={reasonCodes}
              permanentStatuses={permanentStatuses}
              onSet={(s, details) => onSetStatus?.(dealer.seller_id, s, details)}
              onClear={() => onClearStatus?.(dealer.seller_id)}
            />
          )}
          <SalesforceLink salesforceId={dealer.salesforce_id} />
          <RepTag name={dealer.rds_rep} role="RDS rep" />
        </div>
      )}
    </div>
  );
};

type Change = { dir: 'up' | 'down' | 'flat'; pct: number | null };

function computeChange(curr: number | null | undefined, prior: number | null | undefined): Change {
  if (curr == null || prior == null) return { dir: 'flat', pct: null };
  if (prior === 0) {
    if (curr === 0) return { dir: 'flat', pct: null };
    return { dir: 'up', pct: null }; // can't compute %, but it grew from zero
  }
  const diff = curr - prior;
  if (diff === 0) return { dir: 'flat', pct: 0 };
  const pct = (diff / Math.abs(prior)) * 100;
  return { dir: diff > 0 ? 'up' : 'down', pct: Math.abs(pct) };
}

const ChangeBadge: React.FC<{ change: Change }> = ({ change }) => {
  if (change.dir === 'flat' && change.pct == null) {
    return <span className="cv-card-change cv-card-change-flat">—</span>;
  }
  if (change.dir === 'flat') {
    return <span className="cv-card-change cv-card-change-flat">0%</span>;
  }
  const arrow = change.dir === 'up' ? '▲' : '▼';
  const label = change.pct == null ? arrow : `${arrow} ${change.pct.toFixed(0)}%`;
  return <span className={`cv-card-change cv-card-change-${change.dir}`}>{label}</span>;
};

function fmtPct(n: number | null | undefined): string {
  if (n == null) return '–';
  return `${(n * 100).toFixed(1)}%`;
}

// Per-requirement gap toward Core. null = no gap on this requirement; <=0 = met.
function coreGapValue(n?: number | null): string {
  if (n == null || n <= 0) return '✓';
  return String(Math.round(n));
}
